import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Hero global según las maquetas del documento maestro: texto centrado directamente
// sobre la fotografía, sin caja. Misma altura, escala y ubicación en todas las páginas.
export default function Hero({ pre, titulo, bajada, fotos = [], testid = "hero", alto, foco }) {
  const validas = fotos.filter(Boolean);
  const carrusel = validas.length > 1;
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!carrusel) return;
    timer.current = setInterval(() => setIdx((v) => (v + 1) % validas.length), 5500);
    return () => clearInterval(timer.current);
  }, [carrusel, validas.length]);

  const manual = (dir) => {
    clearInterval(timer.current);
    setIdx((v) => (v + dir + validas.length) % validas.length);
  };

  return (
    <section className={`relative overflow-hidden bg-sand-deep ${alto || "h-[440px] md:h-[520px]"}`} data-testid={testid}>
      {validas.map((f, i) => (
        <img
          key={f + i}
          src={f}
          alt=""
          className={`hero-swap absolute inset-0 h-full w-full object-cover object-center ${i === idx ? "opacity-100" : "opacity-0"}`}
          style={foco ? { objectPosition: foco } : undefined}
          data-testid={i === idx ? `${testid}-foto-activa` : undefined}
        />
      ))}

      {/* Degradado oscuro sutil, centrado detrás del título, para asegurar legibilidad sobre cualquier foto */}
      <div
        className="pointer-events-none absolute inset-0 z-[3]"
        style={{ background: "radial-gradient(ellipse 70% 60% at center, rgba(35,31,29,0.42) 0%, rgba(35,31,29,0.18) 45%, rgba(35,31,29,0) 78%)" }}
      />

      {/* Bloque de texto centrado sobre la foto, sin caja (modelo del documento maestro) */}
      <div className="absolute inset-0 z-[5] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
          className="text-center"
          data-testid={`${testid}-bloque-texto`}
        >
          <h1
            className="overflow-hidden font-serif text-5xl font-light tracking-tight text-sand sm:text-6xl lg:text-7xl"
            style={{ textShadow: "0 2px 30px rgba(35,31,29,0.7), 0 1px 4px rgba(35,31,29,0.5), 0 0 60px rgba(35,31,29,0.35)" }}
          >
            <motion.span
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.95, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
              className="block"
            >
              {titulo}
            </motion.span>
          </h1>
          {bajada && (
            <p
              className="mt-4 text-[12px] font-semibold uppercase tracking-[0.24em] text-sand/90"
              style={{ textShadow: "0 1px 10px rgba(35,31,29,0.5)" }}
            >
              {bajada}
            </p>
          )}
        </motion.div>
      </div>

      {carrusel && (
        <>
          <button
            onClick={() => manual(-1)}
            aria-label="Anterior"
            className="absolute left-3 top-1/2 z-10 -translate-y-1/2 border border-sand/50 bg-ink/20 p-2 text-sand backdrop-blur-sm transition-colors hover:bg-ink/35 md:left-5"
            data-testid={`${testid}-prev`}
          >
            <ChevronLeft size={20} strokeWidth={1.6} />
          </button>
          <button
            onClick={() => manual(1)}
            aria-label="Siguiente"
            className="absolute right-3 top-1/2 z-10 -translate-y-1/2 border border-sand/50 bg-ink/20 p-2 text-sand backdrop-blur-sm transition-colors hover:bg-ink/35 md:right-5"
            data-testid={`${testid}-next`}
          >
            <ChevronRight size={20} strokeWidth={1.6} />
          </button>
          <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 gap-2">
            {validas.map((_, i) => (
              <button
                key={i}
                onClick={() => { clearInterval(timer.current); setIdx(i); }}
                aria-label={`Foto ${i + 1}`}
                className={`h-1.5 transition-all duration-300 ${i === idx ? "w-7 bg-sand" : "w-3 bg-sand/50"}`}
                data-testid={`${testid}-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}